import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { OpalApiRequestBody, OpalApiResponseDocument } from '../../schema'
import { boardObjects } from '../../v3/boardObjects'

interface BaseCreateBoardObjectArgs {
  boardId: string
  collectionId: string | null
  position: number
}

interface UseCreateMomentArgs extends BaseCreateBoardObjectArgs {
  momentId: string
}

interface UseCreateRichTextDocumentArgs extends BaseCreateBoardObjectArgs {
  richTextDocumentId: string
}

type UseCreateBoardObjectArgs =
  | UseCreateMomentArgs
  | UseCreateRichTextDocumentArgs

export type UseCreateBoardObjectResult = UseMutationResult<
  OpalApiResponseDocument<'CreateBoardObjectV3', 201>,
  unknown,
  UseCreateBoardObjectArgs,
  unknown
>

const isMomentType = (
  params: UseCreateBoardObjectArgs
): params is UseCreateMomentArgs => params && 'momentId' in params

const isRichTextDocumentType = (
  params: UseCreateBoardObjectArgs
): params is UseCreateRichTextDocumentArgs =>
  params && 'richTextDocumentId' in params

function buildRequestBody(
  params: UseCreateBoardObjectArgs
): OpalApiRequestBody<'CreateBoardObjectV3'> {
  const { position, boardId, collectionId = null } = params

  let boardObject = {}
  if (isMomentType(params)) {
    boardObject = {
      moment: {
        data: {
          id: params.momentId,
          type: 'moment',
        },
      },
    }
  } else if (isRichTextDocumentType(params)) {
    boardObject = {
      rich_text_document: {
        data: {
          id: params.richTextDocumentId,
          type: 'rich_text_document',
        },
      },
    }
  }

  const collectionRelationship =
    collectionId !== null
      ? ({
          id: collectionId,
          type: 'board_object_collection',
        } as const)
      : null

  return {
    data: {
      type: 'board_object',
      attributes: { position },
      relationships: {
        board: {
          data: {
            id: boardId,
            type: 'board',
          },
        },
        board_object_collection: {
          data: collectionRelationship,
        },
        ...boardObject,
      },
    },
  }
}

type MutationParams = {
  onMutate?: (params: UseCreateBoardObjectArgs) => void
  onSuccess?: (
    data: OpalApiResponseDocument<'CreateBoardObjectV3', 201>,
    params: UseCreateBoardObjectArgs
  ) => void
}

export function useCreateBoardObjectMutation({
  onMutate,
  onSuccess,
}: MutationParams): UseCreateBoardObjectResult {
  return useMutation({
    mutationFn: (args: UseCreateBoardObjectArgs) =>
      boardObjects.create(buildRequestBody(args)),
    onSuccess,
    onMutate,
  })
}
